import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useChat } from '../context/ChatContext';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Settings, Plus, Trash2, Save, Zap, Award, Clock, Target, ShieldCheck } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function LoyaltyConfig() {
  const { selectedBrand } = useChat();
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('earn');
  const [simulation, setSimulation] = useState(null);

  const brandId = selectedBrand?.brand_id;

  useEffect(() => {
    if (brandId) fetchConfig();
  }, [brandId]);

  const fetchConfig = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/loyalty/config/${brandId}`, { credentials: 'include' });
      const data = await res.json();
      if (data.success) setConfig(data.config);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const saveConfig = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/loyalty/config/${brandId}`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(config)
      });
      const data = await res.json();
      if (data.success) setConfig(data.config);
    } catch (e) { console.error(e); }
    setSaving(false);
  };

  const runSimulation = async (amount) => {
    try {
      const tierSpend = config?.tiers?.[1]?.min_spend || 25000;
      const res = await fetch(`${API_URL}/api/loyalty/simulate/${brandId}?amount=${amount}&tier_spend=${tierSpend}`, { credentials: 'include' });
      const data = await res.json();
      if (data.success) setSimulation(data.simulation);
    } catch (e) { console.error(e); }
  };

  if (loading) return <div className="p-6 text-muted-foreground">Loading loyalty config...</div>;
  if (!config) return <div className="p-6 text-muted-foreground">No config found for this brand.</div>;

  const tabs = [
    { id: 'earn', label: 'Earn Rules', icon: Zap },
    { id: 'tiers', label: 'Tiers', icon: Award },
    { id: 'expiry', label: 'Expiry & Redemption', icon: Clock },
    { id: 'boosters', label: 'Boosters', icon: Target },
    { id: 'simulate', label: 'Simulate', icon: ShieldCheck },
  ];

  return (
    <div className="p-6 space-y-6 max-w-5xl" data-testid="loyalty-config-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Settings className="w-6 h-6" /> Loyalty Rules Configuration
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Configure earn rates, tiers, boosters, and redemption rules for {selectedBrand?.name || 'this brand'}
          </p>
        </div>
        <Button onClick={saveConfig} disabled={saving} data-testid="save-loyalty-config">
          <Save className="w-4 h-4 mr-2" />{saving ? 'Saving...' : 'Save Changes'}
        </Button>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-border">
        {tabs.map(tab => (
          <button key={tab.id} onClick={() => setActiveTab(tab.id)}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${activeTab === tab.id ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'}`}>
            <tab.icon className="w-4 h-4 inline mr-1.5" />{tab.label}
          </button>
        ))}
      </div>

      {/* Earn Rules */}
      {activeTab === 'earn' && (
        <Card>
          <CardHeader><CardTitle>Point Earning Rules</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium">Points per unit spend</label>
                <Input type="number" value={config.earn_rules.base_rate}
                  onChange={e => setConfig({...config, earn_rules: {...config.earn_rules, base_rate: Number(e.target.value)}})} />
                <p className="text-xs text-muted-foreground mt-1">Points awarded per unit of base_amount spent</p>
              </div>
              <div>
                <label className="text-sm font-medium">Base amount (in Rs)</label>
                <Input type="number" value={config.earn_rules.base_amount}
                  onChange={e => setConfig({...config, earn_rules: {...config.earn_rules, base_amount: Number(e.target.value)}})} />
                <p className="text-xs text-muted-foreground mt-1">E.g., 100 means 1 point per Rs 100</p>
              </div>
              <div>
                <label className="text-sm font-medium">Earn calculated on</label>
                <select className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
                  value={config.earn_rules.earn_on}
                  onChange={e => setConfig({...config, earn_rules: {...config.earn_rules, earn_on: e.target.value}})}>
                  <option value="net_amount">Net Amount</option>
                  <option value="gross_amount">Gross Amount</option>
                  <option value="amount">Bill Amount</option>
                  <option value="loyalty_gross_amount">Loyalty Gross Amount</option>
                </select>
              </div>
              <div className="flex items-center gap-2 pt-5">
                <input type="checkbox" checked={config.earn_rules.round_down}
                  onChange={e => setConfig({...config, earn_rules: {...config.earn_rules, round_down: e.target.checked}})} />
                <label className="text-sm">Round down points (floor)</label>
              </div>
            </div>
            <div className="p-3 bg-muted/50 rounded text-sm">
              <strong>Formula:</strong> Points = (Earn Amount / {config.earn_rules.base_amount}) x {config.earn_rules.base_rate} x Tier Multiplier x Booster Multiplier
            </div>
          </CardContent>
        </Card>
      )}

      {/* Tiers */}
      {activeTab === 'tiers' && (
        <Card>
          <CardHeader>
            <div className="flex justify-between items-center">
              <CardTitle>Tier Configuration</CardTitle>
              <Button size="sm" variant="outline" onClick={() => setConfig({...config, tiers: [...config.tiers, {name: '', min_spend: 0, multiplier: 1.0, benefits: ''}]})}>
                <Plus className="w-4 h-4 mr-1" />Add Tier
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-3">
            <div>
              <label className="text-sm font-medium">Tier evaluated by:</label>
              <select className="ml-2 h-8 px-2 rounded border border-input bg-background text-sm"
                value={config.tier_evaluation}
                onChange={e => setConfig({...config, tier_evaluation: e.target.value})}>
                <option value="lifetime_spend">Lifetime Spend</option>
                <option value="yearly_spend">Yearly Spend</option>
                <option value="points_balance">Points Balance</option>
              </select>
            </div>
            {config.tiers.map((tier, i) => (
              <div key={i} className="flex gap-3 items-center p-3 border rounded-md">
                <Input placeholder="Tier Name" value={tier.name} className="w-32"
                  onChange={e => { const t=[...config.tiers]; t[i]={...t[i], name:e.target.value}; setConfig({...config, tiers:t}); }} />
                <div className="flex-1">
                  <label className="text-xs text-muted-foreground">Min Spend (Rs)</label>
                  <Input type="number" value={tier.min_spend}
                    onChange={e => { const t=[...config.tiers]; t[i]={...t[i], min_spend:Number(e.target.value)}; setConfig({...config, tiers:t}); }} />
                </div>
                <div className="w-24">
                  <label className="text-xs text-muted-foreground">Multiplier</label>
                  <Input type="number" step="0.1" value={tier.multiplier}
                    onChange={e => { const t=[...config.tiers]; t[i]={...t[i], multiplier:Number(e.target.value)}; setConfig({...config, tiers:t}); }} />
                </div>
                <Input placeholder="Benefits" value={tier.benefits || ''} className="flex-1"
                  onChange={e => { const t=[...config.tiers]; t[i]={...t[i], benefits:e.target.value}; setConfig({...config, tiers:t}); }} />
                <Button size="icon" variant="ghost" onClick={() => setConfig({...config, tiers: config.tiers.filter((_, j) => j !== i)})}>
                  <Trash2 className="w-4 h-4 text-destructive" />
                </Button>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Expiry & Redemption */}
      {activeTab === 'expiry' && (
        <Card>
          <CardHeader><CardTitle>Point Expiry & Redemption Rules</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium">Point value (Rs per point)</label>
                <Input type="number" step="0.1" value={config.point_value}
                  onChange={e => setConfig({...config, point_value: Number(e.target.value)})} />
              </div>
              <div>
                <label className="text-sm font-medium">Point expiry (days, 0=never)</label>
                <Input type="number" value={config.point_expiry_days}
                  onChange={e => setConfig({...config, point_expiry_days: Number(e.target.value)})} />
              </div>
              <div>
                <label className="text-sm font-medium">Min points to redeem</label>
                <Input type="number" value={config.min_redeem_points}
                  onChange={e => setConfig({...config, min_redeem_points: Number(e.target.value)})} />
              </div>
              <div>
                <label className="text-sm font-medium">Max bill % redeemable</label>
                <Input type="number" value={config.max_redeem_pct}
                  onChange={e => setConfig({...config, max_redeem_pct: Number(e.target.value)})} />
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Boosters */}
      {activeTab === 'boosters' && (
        <Card>
          <CardHeader>
            <div className="flex justify-between items-center">
              <CardTitle>Booster Schemes</CardTitle>
              <Button size="sm" variant="outline" onClick={() => setConfig({...config, booster_rules: [...(config.booster_rules||[]), {name:'', multiplier:2.0, days:[], start_date:'', end_date:'', stores:[], active:true}]})}>
                <Plus className="w-4 h-4 mr-1" />Add Booster
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-3">
            {(!config.booster_rules || config.booster_rules.length === 0) && (
              <p className="text-sm text-muted-foreground">No boosters configured. Add one to multiply points on specific days/stores.</p>
            )}
            {(config.booster_rules || []).map((booster, i) => (
              <div key={i} className="p-3 border rounded-md space-y-2">
                <div className="flex gap-3 items-center">
                  <Input placeholder="Booster Name" value={booster.name} className="w-48"
                    onChange={e => { const b=[...(config.booster_rules||[])]; b[i]={...b[i], name:e.target.value}; setConfig({...config, booster_rules:b}); }} />
                  <div className="w-24">
                    <label className="text-xs text-muted-foreground">Multiplier</label>
                    <Input type="number" step="0.5" value={booster.multiplier}
                      onChange={e => { const b=[...(config.booster_rules||[])]; b[i]={...b[i], multiplier:Number(e.target.value)}; setConfig({...config, booster_rules:b}); }} />
                  </div>
                  <Input type="date" placeholder="Start" value={booster.start_date || ''}
                    onChange={e => { const b=[...(config.booster_rules||[])]; b[i]={...b[i], start_date:e.target.value}; setConfig({...config, booster_rules:b}); }} />
                  <Input type="date" placeholder="End" value={booster.end_date || ''}
                    onChange={e => { const b=[...(config.booster_rules||[])]; b[i]={...b[i], end_date:e.target.value}; setConfig({...config, booster_rules:b}); }} />
                  <div className="flex items-center gap-1">
                    <input type="checkbox" checked={booster.active}
                      onChange={e => { const b=[...(config.booster_rules||[])]; b[i]={...b[i], active:e.target.checked}; setConfig({...config, booster_rules:b}); }} />
                    <span className="text-xs">Active</span>
                  </div>
                  <Button size="icon" variant="ghost" onClick={() => setConfig({...config, booster_rules: (config.booster_rules||[]).filter((_, j) => j !== i)})}>
                    <Trash2 className="w-4 h-4 text-destructive" />
                  </Button>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Simulate */}
      {activeTab === 'simulate' && (
        <Card>
          <CardHeader><CardTitle>Point Simulation</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-muted-foreground">Test how points will be calculated for a sample transaction.</p>
            <div className="flex gap-3">
              {[1000, 2500, 5000, 10000, 25000].map(amt => (
                <Button key={amt} variant="outline" size="sm" onClick={() => runSimulation(amt)}>
                  Rs {amt.toLocaleString()}
                </Button>
              ))}
            </div>
            {simulation && (
              <div className="p-4 bg-muted/50 rounded-md space-y-2">
                <div className="text-lg font-bold">{simulation.points_earned} points earned</div>
                <div className="text-sm space-y-1">
                  <div>Earn amount: Rs {simulation.earn_amount?.toLocaleString()}</div>
                  <div>Tier: {simulation.tier || 'Base'} (x{simulation.tier_multiplier})</div>
                  <div>Booster: {simulation.booster || 'None'} (x{simulation.booster_multiplier})</div>
                  <div className="font-mono text-xs mt-2 p-2 bg-background rounded">{simulation.breakdown}</div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
